// Ref: https://github.com/thetrevorharmon/sell-things-fast/blob/master/src/context/StoreContext.js

import React, { useState, useEffect, useContext, useRef, useCallback } from "react"
import { scheduleDeferred } from "../utils/deferUntilIdle"

const SHOPIFY_CHECKOUT_STORAGE_KEY = "shopify_checkout_id"

// Timeout duration for API calls (prevents hanging requests)
const API_TIMEOUT_MS = 8000

// Lazy-loaded client to avoid SSR issues with shopify-buy SDK
let client = null

// Helper to wrap promises with timeout - prevents server hanging
const withTimeout = (promise, timeoutMs = API_TIMEOUT_MS, errorMessage = 'Request timed out') => {
  let timeoutId
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(errorMessage)), timeoutMs)
  })
  
  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId))
}

const getClient = async () => {
  if (typeof window === 'undefined') return null
  if (!client) {
    try {
      // Use the unoptimized version of the shopify-buy SDK v3.0 to enable expanding
      //    graphQLClient queries to include additional fields (i.e., quantityAvailable)
      // ref: https://github.com/Shopify/js-buy-sdk/issues/753
      // ref: https://github.com/Shopify/js-buy-sdk#expanding-the-sdk
      const Client = (await import("shopify-buy/index.unoptimized.umd")).default
      client = Client.buildClient({
        storefrontAccessToken: process.env.GATSBY_SHOPIFY_PUBLIC_STOREFRONT_API_TOKEN,
        domain: process.env.GATSBY_SHOPIFY_STOREURL,
      })
    } catch (error) {
      console.error('Failed to initialize Shopify client:', error)
      return null
    }
  }
  return client
}

const initialStoreState = {
  client: null,
  isAdding: false,
  checkout: { lineItems: [] },
}

const StoreContext = React.createContext({
  store: initialStoreState,
  setStore: () => null,
  ensureCheckout: () => Promise.resolve({ client: null, checkout: null }),
})

function setCheckoutInState(checkout, setStore) {
  const isBrowser = typeof window !== "undefined"
  if (isBrowser) {
    localStorage.setItem(SHOPIFY_CHECKOUT_STORAGE_KEY, checkout.id)
  }

  setStore(prevState => {
    return { ...prevState, checkout }
  })
}

/**
 * Checkout init is deferred to idle, so an early click can land before the SDK
 * is ready. Prefer the values captured at render; otherwise wait on the shared
 * initialization rather than dropping the interaction the way the old
 * `if (!client) return` guard did.
 */
const resolveCheckout = async ({ client, checkout, ensureCheckout }) => {
  if (client && checkout?.id) return { client, checkout }

  const ready = await ensureCheckout()
  if (!ready?.client || !ready?.checkout?.id) {
    throw new Error("Shopify checkout unavailable")
  }
  return ready
}

const StoreContextProvider = ({ children }) => {
  const [store, setStore] = useState(initialStoreState)
  // One shared in-flight initialization, so the idle kickoff and an early cart
  // click can't create two checkouts.
  const initPromiseRef = useRef(null)

  const ensureCheckout = useCallback(() => {
    if (initPromiseRef.current) return initPromiseRef.current

    initPromiseRef.current = (async () => {
      // Throw rather than resolve empty: the catch below clears the cached
      // promise, so a later cart interaction gets a fresh attempt.
      const shopifyClient = await getClient()
      if (!shopifyClient) throw new Error("Shopify client unavailable")

      setStore(prevState => ({ ...prevState, client: shopifyClient }))

      // Check for an existing cart. Older builds wrote the string "null" here,
      // so treat that as absent rather than fetching a bogus id.
      const existingCheckoutId = localStorage.getItem(SHOPIFY_CHECKOUT_STORAGE_KEY)

      if (existingCheckoutId && existingCheckoutId !== "null") {
        try {
          const checkout = await withTimeout(
            shopifyClient.checkout.fetch(existingCheckoutId),
            API_TIMEOUT_MS,
            'Checkout fetch timed out'
          )
          // Make sure this cart hasn't already been purchased.
          // Note: In v3.0 Cart API, completedAt always returns null
          // but we keep this check for compatibility
          if (checkout && !checkout.completedAt) {
            setCheckoutInState(checkout, setStore)
            return { client: shopifyClient, checkout }
          }
        } catch (e) {
          // If fetching fails, clear the old checkout ID and create a new one
          console.warn('Checkout fetch failed:', e.message)
          localStorage.removeItem(SHOPIFY_CHECKOUT_STORAGE_KEY)
        }
      }

      const newCheckout = await withTimeout(
        shopifyClient.checkout.create(),
        API_TIMEOUT_MS,
        'Checkout creation timed out'
      )
      setCheckoutInState(newCheckout, setStore)
      return { client: shopifyClient, checkout: newCheckout }
    })().catch(error => {
      console.warn('Checkout initialization error:', error.message)
      // Clear so a later cart interaction can retry instead of being stuck.
      initPromiseRef.current = null
      return { client: null, checkout: null }
    })

    return initPromiseRef.current
  }, [])

  useEffect(() => {
    // The SDK is a ~460KB dynamic import plus a checkout round trip. Cart and
    // checkout routes need it right away; everywhere else nothing on screen
    // depends on it, so keep it off the hydration critical path and let the
    // cart hooks pull it in on demand.
    const pathname = typeof window !== "undefined" ? window.location.pathname : ""
    if (pathname.startsWith("/cart") || pathname.startsWith("/thank-you")) {
      ensureCheckout()
      return undefined
    }

    return scheduleDeferred(() => ensureCheckout(), {
      slowDelay: 4000,
      fastDelay: 1500,
    })
  }, [ensureCheckout])

  return (
    <StoreContext.Provider
      value={{
        store,
        setStore,
        ensureCheckout,
      }}
    >
      {children}
    </StoreContext.Provider>
  )
}

function useStore() {
  const { store } = useContext(StoreContext)
  return store
}

/**
 * Force Shopify checkout initialization now instead of waiting for the idle
 * kickoff. For click handlers that need the SDK before `store.client` lands.
 * Resolves to `{ client, checkout }`, either of which may be null on failure.
 */
function useEnsureCheckout() {
  const { ensureCheckout } = useContext(StoreContext)
  return ensureCheckout
}

function useCartCount() {
  const {
    store: { checkout },
  } = useContext(StoreContext)

  const count = checkout.lineItems.reduce(
    (runningTotal, item) => item.quantity + runningTotal,
    0
  )

  return count
}

function useCartTotals() {
  const {
    store: { checkout },
  } = useContext(StoreContext)

  // shopify-buy v3 maps Cart.cost onto the legacy checkout shape:
  //   lineItemsSubtotalPrice = cost.checkoutChargeAmount (lines, pre-tax)
  //   totalTaxV2             = cost.totalTaxAmount, "0.0" when Shopify has no
  //                            estimate yet (it needs a buyer location)
  //   totalPriceV2           = cost.totalAmount, which already includes any tax
  // The cart page shows tax on its own line whenever total > subtotal, so a
  // tax-inclusive total is never presented as if it were the item price.
  const toAmount = (money) => {
    const n = Number(money?.amount)
    return Number.isFinite(n) ? n : 0
  }
  const cents = (n) => Math.round(n * 100) / 100
  const total = toAmount(checkout.totalPriceV2)
  const subtotal = checkout.lineItemsSubtotalPrice
    ? toAmount(checkout.lineItemsSubtotalPrice)
    : checkout.subtotalPriceV2
      ? toAmount(checkout.subtotalPriceV2)
      : total
  const reportedTax = toAmount(checkout.totalTaxV2)
  const tax = reportedTax > 0 ? reportedTax : cents(Math.max(total - subtotal, 0))

  return {
    subtotal,
    tax,
    total,
  }
}

function useCartItems() {
  const {
    store: { checkout },
  } = useContext(StoreContext)

  return checkout.lineItems
}

function useAddItemToCart() {
  const {
    store: { checkout, client },
    setStore,
    ensureCheckout,
  } = useContext(StoreContext)

  async function addItemToCart(variantId, quantity) {
    if (variantId === "" || !quantity) {
      console.error("Both a variantId and quantity are required.")
      return
    }

    setStore(prevState => {
      return { ...prevState, isAdding: true }
    })

    try {
      const ready = await resolveCheckout({ client, checkout, ensureCheckout })
      const lineItemsToAdd = [{ variantId, quantity: parseInt(quantity, 10) }]

      const newCheckout = await withTimeout(
        ready.client.checkout.addLineItems(ready.checkout.id, lineItemsToAdd),
        API_TIMEOUT_MS,
        'Add to cart timed out'
      )

      setStore(prevState => {
        return { ...prevState, checkout: newCheckout, isAdding: false }
      })
    } catch (error) {
      console.error("Add to cart error:", error.message)
      setStore(prevState => {
        return { ...prevState, isAdding: false }
      })
    }
  }

  return addItemToCart
}

function useUpdateItemInCart() {
  const {
    store: { client, checkout },
    setStore,
    ensureCheckout,
  } = useContext(StoreContext)

  async function updateItemInCart(lineItemId, quantity) {
    try {
      const ready = await resolveCheckout({ client, checkout, ensureCheckout })
      const newCheckout = await withTimeout(
        ready.client.checkout.updateLineItems(ready.checkout.id, [{ id: lineItemId, quantity: parseInt(quantity, 10) }]),
        API_TIMEOUT_MS,
        'Update cart timed out'
      )
      setStore(prevState => ({ ...prevState, checkout: newCheckout }))
    } catch (error) {
      console.error("Update cart error:", error.message)
    }
  }

  return updateItemInCart
}

function useRemoveItemFromCart() {
  const {
    store: { client, checkout },
    setStore,
    ensureCheckout,
  } = useContext(StoreContext)

  async function removeItemFromCart(itemId) {
    try {
      const ready = await resolveCheckout({ client, checkout, ensureCheckout })
      const newCheckout = await withTimeout(
        ready.client.checkout.removeLineItems(ready.checkout.id, [itemId]),
        API_TIMEOUT_MS,
        'Remove from cart timed out'
      )

      setStore(prevState => {
        return { ...prevState, checkout: newCheckout }
      })
    } catch (error) {
      console.error("Remove from cart error:", error.message)
    }
  }

  return removeItemFromCart
}

function useCheckout() {
  const {
    store: { checkout },
  } = useContext(StoreContext)

  return () => {
    if (!checkout?.webUrl) {
      console.error("Checkout not initialized yet.")
      return
    }

    // Replace the default checkout url with the store subdomain url.
    // window.open(checkout.webUrl)

    const checkoutUrl = checkout.webUrl.replace(
      "6deb59.myshopify.com",
      "shop.njtautomation.com"
    )
    
    // Security: Validate checkout URL before opening
    // Only allow Shopify domains to prevent redirect attacks
    const allowedDomains = [
      'shop.njtautomation.com',
      'njtautomation.myshopify.com',
      '6deb59.myshopify.com',
      'checkout.shopify.com'
    ]
    
    try {
      const url = new URL(checkoutUrl)
      const isAllowedDomain = allowedDomains.some(domain => 
        url.hostname === domain || url.hostname.endsWith('.myshopify.com')
      )
      
      if (!isAllowedDomain) {
        console.error('Security: Blocked checkout redirect to unauthorized domain:', url.hostname)
        return
      }
      
      window.open(checkoutUrl, '_blank', 'noopener,noreferrer')
    } catch (e) {
      console.error('Security: Invalid checkout URL:', e)
    }
  }
}

async function getProductAvailability(id) {
  try {
    const shopifyClient = await getClient()
    if (!shopifyClient) return false
    
    const product = await withTimeout(
      shopifyClient.product.fetch(id),
      API_TIMEOUT_MS,
      `Product fetch timed out for ID: ${id}`
    )
    if (product) {
      const variant = product.variants[0]
      if (variant) {
        return variant.available
      }
    }
    return false
  } catch (error) {
    console.warn('getProductAvailability error:', error.message)
    return false
  }
}

async function getInventoryQuantity(productId, productTitle) {
  try {
    const shopifyClient = await getClient()
    if (!shopifyClient) return 0
    
    // Escape special characters in title and wrap in quotes for exact matching
    // This fixes issues with hyphens, spaces, and other special characters
    const escapedTitle = productTitle.replace(/"/g, '\\"')
    
    const query = shopifyClient.graphQLClient.query(root => {
      root.addConnection(
        "products",
        { args: { first: 10, query: `title:"${escapedTitle}"` } },
        product => {
          product.add("title")
          product.addConnection("variants", { args: { first: 1 } }, variant => {
            variant.add("quantityAvailable")
          })
        }
      )
    })

    const response = await withTimeout(
      shopifyClient.graphQLClient.send(query),
      API_TIMEOUT_MS,
      `Inventory query timed out for: ${productTitle}`
    )
    const product = response.model.products.find(p => p.id === productId)
    if (product) {
      return product.variants[0].quantityAvailable
    }

    return 0
  } catch (error) {
    console.warn('getInventoryQuantity error:', error.message)
    return 0
  }
}

async function getInventoryQuantityIndexBySearchQuery(searchQuery) {
  try {
    const shopifyClient = await getClient()
    if (!shopifyClient) return []
    
    // Escape special characters in search query
    const escapedQuery = searchQuery.replace(/"/g, '\\"')
    
    const query = shopifyClient.graphQLClient.query(root => {
      root.addConnection(
        "products",
        { args: { first: 250, query: `title:*${escapedQuery}*` } },
        product => {
          product.add("id")
          product.add("title")
          product.addConnection("variants", { args: { first: 1 } }, variant => {
            variant.add("quantityAvailable")
          })
        }
      )
    })

    const response = await withTimeout(
      shopifyClient.graphQLClient.send(query),
      API_TIMEOUT_MS,
      `Search query timed out for: ${searchQuery}`
    )
    let result = []
    response.model.products.forEach(product => {
      result.push({
        shopifyId: product.id,
        quantityAvailable: product.variants[0].quantityAvailable,
      })
    })

    return result
  } catch (error) {
    console.warn('getInventoryQuantityIndexBySearchQuery error:', error.message)
    return []
  }
}

export {
  StoreContextProvider,
  getProductAvailability,
  getInventoryQuantity,
  getInventoryQuantityIndexBySearchQuery,
  useAddItemToCart,
  useUpdateItemInCart,
  useStore,
  useCartCount,
  useCartItems,
  useCartTotals,
  useRemoveItemFromCart,
  useCheckout,
  useEnsureCheckout,
}
